import React, { useState, useRef, useCallback } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { BulkLetterService, type BulkLetterRequest } from "@/services/bulkLetterService";
import { MasterDocumentService, type MasterDocumentRequest } from "@/services/masterDocumentService";
import { Download, FileText, Loader2, Eye, FolderArchive, Upload, X, Pencil, Check, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

interface ResumeFileMapping {
  resumeIdentifier: string;
  jobTitle: string;
  files: File[]; // multiple files (PDF + Word, v1/v2/v3 etc.)
}

interface BulkLetterData {
  headerInfo: {
    fullName: string;
    phoneNumber: string;
    email: string;
    cityST: string;
    linkedin: string;
    companyName: string;
    recipientName: string;
    jobTitle: string;
  };
  coverLetterBody: string;
  thankYouLetterBody?: string;
  outreachLetterBody?: string;
  resumeIdentifier?: string; // e.g., "Resume1", "Resume2", "Resume3"
}

const NAME_PREFIXES = new Set(['dr', 'dr.', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'prof', 'prof.', 'rev', 'rev.', 'sir', 'hon', 'hon.']);
const CREDENTIAL_SUFFIXES = new Set([
  'mba', 'phd', 'ph.d', 'ph.d.', 'md', 'm.d', 'm.d.', 'jd', 'j.d', 'j.d.',
  'cpa', 'c.p.a', 'cfa', 'cfp', 'pmp', 'pe', 'p.e', 'esq', 'esq.',
  'rn', 'r.n', 'bsn', 'msn', 'dnp', 'dds', 'd.d.s', 'dmd',
  'ms', 'm.s', 'ma', 'm.a', 'mpa', 'mph', 'msw', 'med', 'm.ed',
  'bs', 'b.s', 'ba', 'b.a', 'bba', 'bfa',
  'shrm-cp', 'shrm-scp', 'sphr', 'phr', 'cissp', 'ccna', 'ccnp',
  'lcsw', 'lmft', 'lpc', 'psyd', 'edd', 'ed.d', 'dba',
  'ii', 'iii', 'iv', 'jr', 'jr.', 'sr', 'sr.',
]);

function parseCleanName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.replace(/,/g, ' ').split(/\s+/).filter(Boolean);
  while (parts.length > 1 && NAME_PREFIXES.has(parts[0].toLowerCase())) {
    parts.shift();
  }
  while (parts.length > 1 && CREDENTIAL_SUFFIXES.has(parts[parts.length - 1].toLowerCase().replace(/[.,]/g, ''))) {
    parts.pop();
  }
  return { firstName: parts[0] || 'Client', lastName: parts.length > 1 ? parts[parts.length - 1] : '' };
}

/**
 * Detect whether the pasted JSON uses the new master format (documents array)
 * or the legacy letters format (letters array).
 */
function detectJsonFormat(parsed: any): 'master' | 'legacy' {
  // Explicit documents array or root array → always master
  if (Array.isArray(parsed)) return 'master';
  if (Array.isArray(parsed?.documents)) return 'master';
  // Has clientInfo but no letters array → master (resume-only, linkedin-only, etc.)
  if (parsed?.clientInfo && !Array.isArray(parsed?.letters)) return 'master';
  // Has a letters array → legacy
  if (Array.isArray(parsed?.letters)) return 'legacy';
  // Fallback: if it has clientInfo at all, treat as master
  if (parsed?.clientInfo) return 'master';
  return 'legacy';
}

type ParsedInput =
  | { format: 'master'; parsed: any; request: MasterDocumentRequest }
  | { format: 'legacy'; parsed: any }
  | { format: 'error'; error: string };

/**
 * Single authoritative parser — call at click time in every action handler.
 * Returns format + normalized data so all code paths agree.
 */
function parseInputOnce(jsonInput: string): ParsedInput {
  if (!jsonInput.trim()) {
    return { format: 'error', error: 'Please paste your JSON data' };
  }
  try {
    const parsed = JSON.parse(jsonInput);
    const fmt = detectJsonFormat(parsed);
    if (fmt === 'master') {
      const request = normalizeMasterRequest(parsed);
      return { format: 'master', parsed, request };
    }
    // Legacy: must have letters array
    if (!Array.isArray(parsed?.letters)) {
      return { format: 'error', error: "JSON must contain either a 'documents' array (master format) or a 'letters' array (legacy format)" };
    }
    return { format: 'legacy', parsed };
  } catch (e) {
    return { format: 'error', error: e instanceof Error ? e.message : 'Invalid JSON' };
  }
}

function normalizeDocumentType(documentType: unknown): 'resume' | 'cover-letter' | 'thank-you-letter' | 'outreach-letter' | 'linkedin-profile' | 'executive-bio' | null {
  if (typeof documentType !== 'string') return null;

  const normalized = documentType.trim().toLowerCase().replace(/[\s_]+/g, '-');

  const documentTypeMap: Record<string, 'resume' | 'cover-letter' | 'thank-you-letter' | 'outreach-letter' | 'linkedin-profile' | 'executive-bio'> = {
    'resume': 'resume',
    'cover-letter': 'cover-letter',
    'coverletter': 'cover-letter',
    'thank-you-letter': 'thank-you-letter',
    'thankyouletter': 'thank-you-letter',
    'outreach-letter': 'outreach-letter',
    'outreachletter': 'outreach-letter',
    'linkedin-profile': 'linkedin-profile',
    'linkedinprofile': 'linkedin-profile',
    'linkedin': 'linkedin-profile',
    'executive-bio': 'executive-bio',
    'executivebio': 'executive-bio',
    'bio': 'executive-bio',
  };

  return documentTypeMap[normalized] || null;
}

function normalizeMasterRequest(parsed: any): MasterDocumentRequest {
  const rawDocuments = Array.isArray(parsed) ? parsed : parsed?.documents;

  if (!Array.isArray(rawDocuments)) {
    throw new Error("Master JSON must contain a 'documents' array");
  }

  const candidates = [
    parsed?.clientInfo,
    parsed,
    ...rawDocuments.flatMap((doc: any) => [doc, doc?.headerInfo, doc?.data]),
  ].filter(Boolean);

  const pickFirstString = (...paths: string[]) => {
    for (const candidate of candidates) {
      for (const path of paths) {
        const value = path.split('.').reduce<any>((acc, key) => acc?.[key], candidate);
        if (typeof value === 'string' && value.trim()) {
          return value.trim();
        }
      }
    }
    return '';
  };

  const firstName = pickFirstString('LinkedIn_First_Name', 'LinkedIn First Name');
  const lastName = pickFirstString('LinkedIn_Last_Name', 'LinkedIn Last Name');
  const fullName = pickFirstString('fullName', 'name', 'Name') || [firstName, lastName].filter(Boolean).join(' ') || 'Client';
  const city = pickFirstString('City', 'city');
  const state = pickFirstString('State', 'state');
  const cityST = pickFirstString('cityST') || [city, state].filter(Boolean).join(city && state ? ', ' : '');

  return {
    clientInfo: {
      fullName,
      phoneNumber: pickFirstString('phoneNumber', 'phone', 'Phone'),
      email: pickFirstString('email', 'Email'),
      cityST,
      linkedin: pickFirstString('linkedin', 'linkedinUrl', 'LinkedIn', 'LinkedIn_URL', 'LinkedIn URL'),
    },
    documents: rawDocuments.map((doc: any, index: number) => ({
      ...doc,
      documentType: (normalizeDocumentType(doc?.documentType) || doc?.documentType || 'resume') as MasterDocumentRequest['documents'][number]['documentType'],
      resumeIdentifier: doc?.resumeIdentifier || `Resume${index + 1}`,
    })),
  };
}


export default function BulkLetterGenerator() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [jsonInput, setJsonInput] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [generatingZip, setGeneratingZip] = useState(false);
  const [generatingForViewer, setGeneratingForViewer] = useState(false);
  const [letterTypes, setLetterTypes] = useState({
    coverLetter: true,
    thankYouLetter: true,
    outreachLetter: true,
  });
  const [docTypes, setDocTypes] = useState({
    resume: true,
    coverLetter: true,
    thankYouLetter: true,
    outreachLetter: true,
    linkedinProfile: true,
    executiveBio: true,
  });
  const [resumeFiles, setResumeFiles] = useState<ResumeFileMapping[]>([]);
  const [rootFiles, setRootFiles] = useState<File[]>([]); // LinkedIn, executive bio — go in ZIP root
  // Map from original jobTitle -> custom folder name
  const [folderNameOverrides, setFolderNameOverrides] = useState<Record<string, string>>({});
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editingFolderValue, setEditingFolderValue] = useState('');

  // Membership auto-prompt state
  const [showMembershipPrompt, setShowMembershipPrompt] = useState(false);
  const [pendingClientInfo, setPendingClientInfo] = useState<{ fullName: string; firstName: string; lastName: string; email: string; phone: string } | null>(null);
  const [isCheckingMembership, setIsCheckingMembership] = useState(false);
  const [isCreatingMembership, setIsCreatingMembership] = useState(false);
  const [pendingZipAction, setPendingZipAction] = useState<(() => Promise<void>) | null>(null);

  const PRO_PLAN_ID = '882d25a0-7808-4630-9324-61073bd03fba';

  // ── Cross-platform account-create options (mirrors AdminQuickOnboard) ──
  // Defaults: all three platforms checked, JobIntel on Trial, both default
  // Academy courses granted. The dialog lets the operator un-check any of
  // them per-client before the accounts get created.
  const JOBINTEL_PLANS_BLG = [
    { key: "trial",     label: "Trial (default)" },
    { key: "seeker",    label: "Seeker" },
    { key: "pro",       label: "Pro" },
    { key: "executive", label: "Executive" },
    { key: "elite",     label: "Elite" },
  ];
  const ACADEMY_COURSES_BLG = [
    { id: "bd3c076f-aead-4b21-97d5-90849027bac9", label: "The Science of Getting Job Interviews Masterclass 2026" },
    { id: "1de8beb3-38c1-4da2-ae72-6e312565015d", label: "Executive Jobs on Demand: Mini Masterclass Series 2026" },
  ];
  const [twEnabled, setTwEnabled] = useState(true);
  const [jiEnabled, setJiEnabled] = useState(true);
  const [jiPlan, setJiPlan]       = useState('trial');
  const [acEnabled, setAcEnabled] = useState(true);
  const [acCourseIds, setAcCourseIds] = useState<string[]>(ACADEMY_COURSES_BLG.map((c) => c.id));

  // Get the effective folder name for an entry
  const getFolderName = (jobTitle: string) => {
    return folderNameOverrides[jobTitle] || jobTitle;
  };

  const startEditingFolder = (jobTitle: string) => {
    setEditingFolder(jobTitle);
    setEditingFolderValue(getFolderName(jobTitle));
  };

  const commitFolderEdit = (originalJobTitle: string) => {
    const trimmed = editingFolderValue.trim();
    if (trimmed && trimmed !== originalJobTitle) {
      setFolderNameOverrides(prev => ({ ...prev, [originalJobTitle]: trimmed }));
      // Update resumeFiles to use the new folder name
      setResumeFiles(prev => prev.map(r => {
        const entryJobTitle = getLetterEntries().find(e => e.resumeIdentifier === r.resumeIdentifier)?.jobTitle;
        if (entryJobTitle === originalJobTitle) {
          return { ...r, jobTitle: trimmed };
        }
        return r;
      }));
    } else if (trimmed === originalJobTitle) {
      // Reset override
      setFolderNameOverrides(prev => {
        const next = { ...prev };
        delete next[originalJobTitle];
        return next;
      });
    }
    setEditingFolder(null);
  };

  // Parse JSON to get resume identifiers and job titles for mapping
  // Supports both legacy (letters[]) and master (documents[]) formats
  const getLetterEntries = (): { resumeIdentifier: string; jobTitle: string }[] => {
    try {
      const parsed = JSON.parse(jsonInput);
      const format = detectJsonFormat(parsed);

      if (format === 'master') {
        const request = normalizeMasterRequest(parsed);
        const seen = new Map<string, string>();

        request.documents.forEach((doc, i) => {
          const jt = doc.jobTitle || '';
          if (jt && !seen.has(jt)) {
            seen.set(jt, doc.resumeIdentifier || `Resume${i + 1}`);
          }
        });

        return Array.from(seen.entries()).map(([jobTitle, resumeIdentifier]) => ({
          resumeIdentifier,
          jobTitle,
        }));
      }

      if (parsed?.letters && Array.isArray(parsed.letters)) {
        return parsed.letters.map((l: any, i: number) => ({
          resumeIdentifier: l.resumeIdentifier || `Resume${i + 1}`,
          jobTitle: l.headerInfo?.jobTitle || 'Unknown'
        }));
      }
    } catch {}
    return [];
  };

  const handleResumeUpload = (resumeIdentifier: string, jobTitle: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const folderName = getFolderName(jobTitle);
    setResumeFiles(prev => {
      const existing = prev.find(r => r.resumeIdentifier === resumeIdentifier);
      if (existing) {
        return prev.map(r => r.resumeIdentifier === resumeIdentifier
          ? { ...r, jobTitle: folderName, files: [...r.files, file] }
          : r
        );
      }
      return [...prev, { resumeIdentifier, jobTitle: folderName, files: [file] }];
    });
  };

  const removeResumeFile = (resumeIdentifier: string, fileIndex?: number) => {
    if (fileIndex !== undefined) {
      setResumeFiles(prev => prev.map(r => {
        if (r.resumeIdentifier !== resumeIdentifier) return r;
        const newFiles = r.files.filter((_, i) => i !== fileIndex);
        return { ...r, files: newFiles };
      }).filter(r => r.files.length > 0));
    } else {
      setResumeFiles(prev => prev.filter(r => r.resumeIdentifier !== resumeIdentifier));
    }
  };

  const [isDragging, setIsDragging] = useState(false);
  const [isAIMatching, setIsAIMatching] = useState(false);

  // Local fallback matching (substring-based)
  const matchFileToEntryLocal = (filename: string, entries: { resumeIdentifier: string; jobTitle: string }[]): { resumeIdentifier: string; jobTitle: string } | null => {
    const nameNoExt = filename.replace(/\.[^.]+$/, '').trim();
    const nameStripped = nameNoExt.replace(/[\s_\-\(]*v\d+[\)]*$/i, '').trim().toLowerCase();

    for (const entry of entries) {
      const titleLower = entry.jobTitle.toLowerCase().trim();
      const idLower = entry.resumeIdentifier.toLowerCase().trim();
      if (nameStripped.includes(titleLower) || nameStripped.includes(idLower) ||
          nameNoExt.toLowerCase().includes(titleLower) || nameNoExt.toLowerCase().includes(idLower)) {
        return entry;
      }
    }

    const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedName = normalize(nameStripped);
    for (const entry of entries) {
      if (normalizedName.includes(normalize(entry.jobTitle)) || normalizedName.includes(normalize(entry.resumeIdentifier))) {
        return entry;
      }
    }

    return null;
  };

  const applyMatches = (validFiles: File[], matchMap: Map<string, string | null>, entries: { resumeIdentifier: string; jobTitle: string }[]) => {
    const matched: string[] = [];
    const unmatched: string[] = [];

    for (const file of validFiles) {
      const resumeId = matchMap.get(file.name);
      const entry = resumeId ? entries.find(e => e.resumeIdentifier === resumeId) : null;

      if (entry) {
        const folderName = getFolderName(entry.jobTitle);
        setResumeFiles(prev => {
          const existing = prev.find(r => r.resumeIdentifier === entry.resumeIdentifier);
          if (existing) {
            if (existing.files.some(f => f.name === file.name)) return prev;
            return prev.map(r => r.resumeIdentifier === entry.resumeIdentifier
              ? { ...r, files: [...r.files, file] }
              : r
            );
          }
          return [...prev, { resumeIdentifier: entry.resumeIdentifier, jobTitle: folderName, files: [file] }];
        });
        matched.push(`${file.name} → ${folderName}`);
      } else {
        unmatched.push(file.name);
      }
    }

    if (matched.length > 0) {
      toast({ title: `${matched.length} resume(s) matched`, description: matched.join('\n') });
    }
    if (unmatched.length > 0) {
      toast({ title: `${unmatched.length} file(s) not matched`, description: `Could not match: ${unmatched.join(', ')}. Use the individual upload buttons instead.`, variant: "destructive" });
    }
  };

  const handleDropFiles = async (files: FileList | File[]) => {
    const entries = getLetterEntries();
    if (entries.length === 0) {
      toast({ title: "No JSON entries", description: "Paste valid JSON first so files can be matched to entries.", variant: "destructive" });
      return;
    }

    const validExts = ['.pdf', '.doc', '.docx'];
    const validFiles = Array.from(files).filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      return validExts.includes(ext);
    });

    if (validFiles.length === 0) return;

    // Try AI matching first
    setIsAIMatching(true);
    toast({ title: "Matching resumes with AI...", description: `Analyzing ${validFiles.length} file(s)...` });

    try {
      const { data, error } = await supabase.functions.invoke('match-resumes-to-jobs', {
        body: {
          filenames: validFiles.map(f => f.name),
          entries: entries.map(e => ({ resumeIdentifier: e.resumeIdentifier, jobTitle: e.jobTitle })),
        },
      });

      if (error) throw error;
      if (!data?.matches || !Array.isArray(data.matches)) throw new Error("Invalid AI response");

      // Build mapping from AI response
      const matchMap = new Map<string, string | null>();
      for (const m of data.matches) {
        matchMap.set(m.filename, m.resumeIdentifier);
      }

      // Any files the AI didn't mention → null
      for (const f of validFiles) {
        if (!matchMap.has(f.name)) matchMap.set(f.name, null);
      }

      applyMatches(validFiles, matchMap, entries);
    } catch (err) {
      console.error('AI matching failed, falling back to local matching:', err);
      toast({ title: "AI matching unavailable", description: "Using local matching as fallback." });

      // Fallback: local substring matching
      const matchMap = new Map<string, string | null>();
      for (const file of validFiles) {
        const entry = matchFileToEntryLocal(file.name, entries);
        matchMap.set(file.name, entry?.resumeIdentifier ?? null);
      }
      applyMatches(validFiles, matchMap, entries);
    } finally {
      setIsAIMatching(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleDropFiles(e.dataTransfer.files);
  };

  const getJsonTemplate = () => {
    return JSON.stringify({
      clientInfo: {
        fullName: "John Doe",
        phoneNumber: "(555) 123-4567",
        email: "john.doe@email.com",
        cityST: "New York, NY",
        linkedin: "https://linkedin.com/in/johndoe"
      },
      documents: [
        {
          documentType: "resume",
          resumeIdentifier: "Resume1",
          jobTitle: "Software Engineer",
          companyName: "Target Company Inc",
          data: {
            Name: "John Doe",
            Phone: "(555) 123-4567",
            Email: "john.doe@email.com",
            City: "New York",
            State: "NY",
            LinkedIn: "https://linkedin.com/in/johndoe",
            CareerHighlights: [
              { chtitle: "Led cross-functional team", chdescription: "Managed a team of 12 engineers delivering enterprise platform" }
            ],
            ProfessionalExperience: [
              {
                job_title: "Software Engineer",
                company: "Tech Corp",
                location: "New York, NY",
                start_date: "Jan 2020",
                end_date: "Present",
                Descriptions: [
                  { Description: "Built and deployed microservices handling 10M+ requests/day" }
                ]
              }
            ],
            SkillRows: [
              { Skill1: "JavaScript", Skill2: "TypeScript", Skill3: "React", Skill4: "Node.js" }
            ]
          }
        },
        {
          documentType: "cover-letter",
          resumeIdentifier: "Resume1",
          jobTitle: "Software Engineer",
          companyName: "Target Company Inc",
          recipientName: "Jane Smith",
          data: { body: "I am writing to express my interest in the Software Engineer position..." }
        },
        {
          documentType: "thank-you-letter",
          resumeIdentifier: "Resume1",
          jobTitle: "Software Engineer",
          companyName: "Target Company Inc",
          recipientName: "Jane Smith",
          data: { body: "Thank you for taking the time to interview me for the Software Engineer position..." }
        },
        {
          documentType: "outreach-letter",
          resumeIdentifier: "Resume1",
          jobTitle: "Software Engineer",
          companyName: "Target Company Inc",
          recipientName: "Jane Smith",
          data: { body: "I hope this message finds you well. I am reaching out regarding potential opportunities..." }
        },
        {
          documentType: "linkedin-profile",
          data: { body: "Experienced Software Engineer with 10+ years in full-stack development..." }
        },
        {
          documentType: "executive-bio",
          data: { body: "John Doe is a seasoned technology leader with expertise in enterprise software architecture..." }
        }
      ]
    }, null, 2);
  };

  const getLegacyJsonTemplate = () => {
    return JSON.stringify({
      letters: [
        {
          resumeIdentifier: "Resume1",
          headerInfo: {
            fullName: "John Doe",
            phoneNumber: "(555) 123-4567",
            email: "john.doe@email.com",
            cityST: "New York, NY",
            linkedin: "https://linkedin.com/in/johndoe",
            companyName: "Target Company Inc",
            recipientName: "Jane Smith",
            jobTitle: "Software Engineer"
          },
          coverLetterBody: "I am writing to express my interest in the Software Engineer position...",
          thankYouLetterBody: "Thank you for taking the time to interview me for the Software Engineer position...",
          outreachLetterBody: "I hope this message finds you well. I am reaching out regarding potential opportunities..."
        }
      ]
    }, null, 2);
  };

  // ── Master JSON generation (new format) ──

  const generateMasterZip = async () => {
    if (!jsonInput.trim()) {
      toast({ title: "Missing JSON Data", description: "Please paste your JSON data", variant: "destructive" });
      return;
    }

    setGeneratingZip(true);
    try {
      const parsed = JSON.parse(jsonInput);
      const request = normalizeMasterRequest(parsed);

      const docTypeMap: Record<string, keyof typeof docTypes> = {
        'resume': 'resume',
        'cover-letter': 'coverLetter',
        'thank-you-letter': 'thankYouLetter',
        'outreach-letter': 'outreachLetter',
        'linkedin-profile': 'linkedinProfile',
        'executive-bio': 'executiveBio',
      };

      const filteredDocs = request.documents.filter((doc) => {
        const key = docTypeMap[doc.documentType];
        return key ? docTypes[key] : true;
      });

      if (filteredDocs.length === 0) {
        toast({ title: "No Documents Selected", description: "Enable at least one document type", variant: "destructive" });
        return;
      }

      toast({ title: "Generating Documents", description: `Processing ${filteredDocs.length} documents...` });

      const normalizedRequest: MasterDocumentRequest = {
        clientInfo: request.clientInfo,
        documents: filteredDocs,
      };

      const response = await MasterDocumentService.generateDocuments(normalizedRequest);

      const { firstName, lastName } = parseCleanName(request.clientInfo.fullName);
      const zipName = `${firstName} ${lastName} - Master Documents.zip`.replace(/\s+/g, ' ');

      await MasterDocumentService.downloadAsZip(
        response.files,
        zipName,
        resumeFiles,
        folderNameOverrides,
        rootFiles
      );

      const totalResumeFiles = resumeFiles.reduce((sum, r) => sum + r.files.length, 0);
      const totalRootFiles = rootFiles.length;
      toast({
        title: "ZIP Downloaded!",
        description: `${response.files.length} documents${totalResumeFiles > 0 ? ` + ${totalResumeFiles} uploaded resumes` : ''}${totalRootFiles > 0 ? ` + ${totalRootFiles} uploaded file(s)` : ''} organized into folders`,
      });
    } catch (error) {
      console.error('Error generating master ZIP:', error);
      toast({ title: "Generation Failed", description: error instanceof Error ? error.message : "Failed to generate documents", variant: "destructive" });
    } finally {
      setGeneratingZip(false);
    }
  };

  const generateMasterIndividual = async () => {
    if (!jsonInput.trim()) {
      toast({ title: "Missing JSON Data", description: "Please paste your JSON data", variant: "destructive" });
      return;
    }

    setGenerating(true);
    try {
      const parsed = JSON.parse(jsonInput);
      const request = normalizeMasterRequest(parsed);

      const docTypeMap: Record<string, keyof typeof docTypes> = {
        'resume': 'resume',
        'cover-letter': 'coverLetter',
        'thank-you-letter': 'thankYouLetter',
        'outreach-letter': 'outreachLetter',
        'linkedin-profile': 'linkedinProfile',
        'executive-bio': 'executiveBio',
      };

      const filteredDocs = request.documents.filter((doc) => {
        const key = docTypeMap[doc.documentType];
        return key ? docTypes[key] : true;
      });

      if (filteredDocs.length === 0) {
        toast({ title: "No Documents Selected", description: "Enable at least one document type", variant: "destructive" });
        return;
      }

      toast({ title: "Generating Documents", description: `Processing ${filteredDocs.length} documents...` });

      const normalizedRequest: MasterDocumentRequest = {
        clientInfo: request.clientInfo,
        documents: filteredDocs,
      };

      const response = await MasterDocumentService.generateDocuments(normalizedRequest);

      for (const file of response.files) {
        MasterDocumentService.downloadFileFromBase64(file.filename, file.data);
      }

      toast({ title: "Documents Generated!", description: `${response.files.length} documents downloaded` });
    } catch (error) {
      console.error('Error generating master documents:', error);
      toast({ title: "Generation Failed", description: error instanceof Error ? error.message : "Failed to generate documents", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  // ── Legacy format generation (unchanged logic) ──

  const generateBulkLetters = async () => {
    if (!jsonInput.trim()) {
      toast({ title: "Missing JSON Data", description: "Please paste your JSON data to generate letters", variant: "destructive" });
      return;
    }
    // Filter letter types — if none selected, skip letter generation gracefully

    setGenerating(true);
    try {
      const parsedData = JSON.parse(jsonInput) as BulkLetterRequest;
      if (!parsedData.letters || !Array.isArray(parsedData.letters)) {
        throw new Error("JSON must contain a 'letters' array");
      }

      const totalLetters = parsedData.letters.length * Object.values(letterTypes).filter(Boolean).length;
      toast({ title: "Generating Letters", description: `Processing ${totalLetters} letters...` });

      const requestData: BulkLetterRequest = { ...parsedData, letterTypes };
      const response = await BulkLetterService.generateBulkLetters(requestData);

      for (const file of response.files) {
        BulkLetterService.downloadFileFromBase64(file.filename, file.data);
      }

      toast({ title: "Letters Generated Successfully", description: `${response.files.length} letters downloaded!` });
    } catch (error) {
      console.error('Error generating bulk letters:', error);
      toast({ title: "Generation Failed", description: error instanceof Error ? error.message : "Failed to parse JSON or generate letters", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const generateBulkLettersAsZip = async () => {
    if (!jsonInput.trim()) {
      toast({ title: "Missing JSON Data", description: "Please paste your JSON data to generate letters", variant: "destructive" });
      return;
    }
    // Filter letter types — if none selected, skip letter generation gracefully

    setGeneratingZip(true);
    try {
      const parsedData = JSON.parse(jsonInput) as BulkLetterRequest;
      if (!parsedData.letters || !Array.isArray(parsedData.letters)) {
        throw new Error("JSON must contain a 'letters' array");
      }

      const totalLetters = parsedData.letters.length * Object.values(letterTypes).filter(Boolean).length;
      toast({ title: "Generating ZIP", description: `Processing ${totalLetters} letters into organized folders...` });

      const requestData: BulkLetterRequest = { ...parsedData, letterTypes };
      const response = await BulkLetterService.generateBulkLetters(requestData);

      const rawName = parsedData.letters[0]?.headerInfo?.fullName || 'Client';
      const { firstName, lastName } = parseCleanName(rawName);
      const zipName = `${firstName} ${lastName} - Bulk Letters.zip`.replace(/\s+/g, ' ');

      await BulkLetterService.downloadAsZip(response.files, zipName, resumeFiles, folderNameOverrides, rootFiles);

      const totalResumeFiles = resumeFiles.reduce((sum, r) => sum + r.files.length, 0);
      const totalRootFiles = rootFiles.length;
      toast({ title: "ZIP Downloaded!", description: `${response.files.length} letters${totalResumeFiles > 0 ? ` + ${totalResumeFiles} resumes` : ''}${totalRootFiles > 0 ? ` + ${totalRootFiles} general file(s)` : ''} organized into folders by job title` });
    } catch (error) {
      console.error('Error generating ZIP:', error);
      toast({ title: "Generation Failed", description: error instanceof Error ? error.message : "Failed to generate letters", variant: "destructive" });
    } finally {
      setGeneratingZip(false);
    }
  };

  const generateAndOpenInViewer = async () => {
    const result = parseInputOnce(jsonInput);
    if (result.format === 'error') {
      toast({ title: "Invalid Input", description: result.error, variant: "destructive" });
      return;
    }

    setGeneratingForViewer(true);
    try {
      if (result.format === 'master') {
        const request = result.request;

        const docTypeMap: Record<string, keyof typeof docTypes> = {
          'resume': 'resume',
          'cover-letter': 'coverLetter',
          'thank-you-letter': 'thankYouLetter',
          'outreach-letter': 'outreachLetter',
          'linkedin-profile': 'linkedinProfile',
          'executive-bio': 'executiveBio',
        };

        const filteredDocs = request.documents.filter((doc) => {
          const key = docTypeMap[doc.documentType];
          return key ? docTypes[key] : true;
        });

        if (filteredDocs.length === 0) {
          toast({ title: "No Documents Selected", description: "Enable at least one document type", variant: "destructive" });
          return;
        }

        toast({ title: "Generating Documents", description: `Processing ${filteredDocs.length} documents for viewer...` });

        const response = await MasterDocumentService.generateAndStoreDocuments({
          clientInfo: request.clientInfo,
          documents: filteredDocs,
        });
        toast({ title: "Documents Generated", description: `${response.totalFiles} documents generated. Opening in viewer...` });
        navigate('/word-document-viewer', { state: { bulkLetters: response.files, source: 'bulk-generator' } });
      } else {
        // Legacy format viewer flow

        const parsedData = result.parsed as BulkLetterRequest;
        if (!parsedData.letters || !Array.isArray(parsedData.letters)) {
          throw new Error("JSON must contain a 'letters' array");
        }

        const totalLetters = parsedData.letters.length * Object.values(letterTypes).filter(Boolean).length;
        toast({ title: "Generating Letters", description: `Processing ${totalLetters} letters for viewer...` });

        const requestData: BulkLetterRequest = { ...parsedData, letterTypes };

        try {
          const response = await BulkLetterService.generateAndStoreBulkLetters(requestData);
          toast({ title: "Letters Generated Successfully", description: `${response.totalFiles} letters generated. Opening in document viewer...` });
          navigate('/word-document-viewer', { state: { bulkLetters: response.files, source: 'bulk-generator' } });
        } catch (storageError) {
          console.log('Storage failed, falling back to direct download:', storageError);
          const response = await BulkLetterService.generateBulkLetters(requestData);
          for (const file of response.files) {
            BulkLetterService.downloadFileFromBase64(file.filename, file.data);
          }
          toast({ title: "Letters Downloaded", description: `${response.files.length} letters downloaded! (Login required for viewer)` });
        }
      }
    } catch (error) {
      console.error('Error generating documents for viewer:', error);
      toast({ title: "Generation Failed", description: error instanceof Error ? error.message : "Failed to parse JSON or generate documents", variant: "destructive" });
    } finally {
      setGeneratingForViewer(false);
    }
  };

  // ── Detect format and dispatch ──

  const isMasterFormat = (() => {
    try {
      const parsed = JSON.parse(jsonInput);
      return detectJsonFormat(parsed) === 'master';
    } catch {
      return false;
    }
  })();

  const handleGenerate = () => {
    const result = parseInputOnce(jsonInput);
    if (result.format === 'error') {
      toast({ title: "Invalid Input", description: result.error, variant: "destructive" });
      return;
    }
    if (result.format === 'master') {
      generateMasterIndividual();
    } else {
      generateBulkLetters();
    }
  };

  const executeZipDownload = async () => {
    const result = parseInputOnce(jsonInput);
    if (result.format === 'error') {
      toast({ title: "Invalid Input", description: result.error, variant: "destructive" });
      return;
    }
    if (result.format === 'master') {
      await generateMasterZip();
    } else {
      await generateBulkLettersAsZip();
    }
  };

  // Extract client info from parsed JSON (master or legacy format)
  const getClientInfoFromJson = (): { fullName: string; firstName: string; lastName: string; email: string; phone: string } | null => {
    const result = parseInputOnce(jsonInput);
    if (result.format === 'error') return null;

    let fullName = '';
    let email = '';
    let phone = '';

    if (result.format === 'master') {
      fullName = result.request.clientInfo.fullName || '';
      email = result.request.clientInfo.email || '';
      phone = result.request.clientInfo.phoneNumber || '';
    } else if (result.parsed?.letters?.[0]?.headerInfo) {
      const h = result.parsed.letters[0].headerInfo;
      fullName = h.fullName || '';
      email = h.email || '';
      phone = h.phoneNumber || '';
    }

    if (!email) return null;

    const { firstName, lastName } = parseCleanName(fullName);
    return { fullName, firstName, lastName, email, phone };
  };

  const handleGenerateZip = async () => {
    const clientInfo = getClientInfoFromJson();
    if (!clientInfo || !clientInfo.email) {
      // No client info found, just proceed with ZIP
      executeZipDownload();
      return;
    }

    setIsCheckingMembership(true);
    try {
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .ilike('email', clientInfo.email.trim())
        .maybeSingle();

      if (existingProfile) {
        // User already exists, proceed silently
        await executeZipDownload();
      } else {
        // User doesn't exist — show prompt
        setPendingClientInfo(clientInfo);
        setPendingZipAction(() => executeZipDownload);
        setShowMembershipPrompt(true);
      }
    } catch (err) {
      console.error('Error checking membership:', err);
      // On error, just proceed with download
      await executeZipDownload();
    } finally {
      setIsCheckingMembership(false);
    }
  };

  const handleConfirmCreateMembership = async () => {
    if (!pendingClientInfo) return;

    const phoneDigits = pendingClientInfo.phone.replace(/\D/g, '');
    if (phoneDigits.length < 4) {
      toast({
        title: "Cannot create account",
        description: "Phone number must have at least 4 digits for password generation.",
        variant: "destructive",
      });
      return;
    }

    if (!twEnabled && !jiEnabled && !acEnabled) {
      toast({
        title: "Pick at least one platform",
        description: "Check off TailorWiz, JobIntel 360, or Jobs On Demand Academy.",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingMembership(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: {
          action: 'quick_onboard_create',
          userData: {
            first_name: pendingClientInfo.firstName,
            last_name: pendingClientInfo.lastName,
            email: pendingClientInfo.email,
            phone: pendingClientInfo.phone,
            job_title: '',
            industry: '',
            location: '',
            years_experience: null,
            linkedin_url: '',
          },
          planId: PRO_PLAN_ID,
          // Mirrors AdminQuickOnboard. The edge function knows how to fan
          // out account creation to all three platforms in one call.
          platforms: {
            tailorwiz: { enabled: twEnabled },
            jobintel:  { enabled: jiEnabled, plan: jiPlan },
            academy:   { enabled: acEnabled, courseIds: acCourseIds },
          },
        },
      });

      if (error) throw error;

      if (data?.error) {
        toast({
          title: "Account creation failed",
          description: data.error,
          variant: "destructive",
        });
      } else if (data?.success) {
        // Surface per-platform status so the operator sees exactly which
        // platforms got the account, which were already-member skips
        // (rendered green), and which actually failed.
        const platforms = (data?.platforms || {}) as Record<string, { ok?: boolean; skipped?: boolean; reason?: string; error?: string }>;
        const statusLine = (label: string, p?: { ok?: boolean; skipped?: boolean; reason?: string; error?: string }): string => {
          if (!p)                              return '';
          if (p.reason === 'already_member')   return `${label}: 🟢 Already a member`;
          if (p.reason === 'not_configured')   return `${label}: ⚠ Not configured`;
          if (p.skipped && p.reason === 'disabled') return '';
          if (p.skipped)                       return `${label}: skipped`;
          if (p.ok)                            return `${label}: ✅ Created`;
          if (p.error)                         return `${label}: ❌ ${p.error}`;
          return `${label}: ?`;
        };
        const summary = [
          twEnabled ? statusLine('TailorWiz',    platforms.tailorwiz) : '',
          jiEnabled ? statusLine('JobIntel 360', platforms.jobintel)  : '',
          acEnabled ? statusLine('Academy',      platforms.academy)   : '',
        ].filter(Boolean).join(' · ');
        toast({
          title: "Accounts processed",
          description: `${summary || 'Done'}  ·  Email: ${data.email}  ·  Password: ${data.password}`,
          duration: 14000,
        });
      }
    } catch (err) {
      console.error('Error creating membership:', err);
      toast({
        title: "Account creation failed",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingMembership(false);
      setShowMembershipPrompt(false);
      setPendingClientInfo(null);
      // Proceed with ZIP download regardless
      if (pendingZipAction) {
        await pendingZipAction();
        setPendingZipAction(null);
      }
    }
  };

  const handleSkipMembership = async () => {
    setShowMembershipPrompt(false);
    setPendingClientInfo(null);
    if (pendingZipAction) {
      await pendingZipAction();
      setPendingZipAction(null);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <PageHeader
        title="Master Document Generator"
        description="Generate resumes, cover letters, thank you letters, outreach letters, LinkedIn profiles, and executive bios in one batch"
        backTo="/cover-letter-builder"
      />

      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>JSON Data Input</CardTitle>
            <CardDescription>
              Paste a <strong>Master JSON</strong> (with <code>clientInfo</code> + <code>documents</code>) to generate all document types, or use the <strong>legacy format</strong> (<code>letters</code> array) for letters only.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Document Type Selection — shown for master format */}
            {isMasterFormat ? (
              <div className="space-y-3">
                <Label className="text-base font-medium">Document Types to Generate</Label>
                <div className="flex flex-wrap gap-4">
                  {([
                    ['resume', 'Resumes'],
                    ['coverLetter', 'Cover Letters'],
                    ['thankYouLetter', 'Thank You Letters'],
                    ['outreachLetter', 'Outreach Letters'],
                    ['linkedinProfile', 'LinkedIn Profiles'],
                    ['executiveBio', 'Executive Bios'],
                  ] as [keyof typeof docTypes, string][]).map(([key, label]) => (
                    <div key={key} className="flex items-center space-x-2">
                      <Checkbox
                        id={`doc-${key}`}
                        checked={docTypes[key]}
                        onCheckedChange={(checked) =>
                          setDocTypes(prev => ({ ...prev, [key]: checked as boolean }))
                        }
                      />
                      <Label htmlFor={`doc-${key}`}>{label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <Label className="text-base font-medium">Letter Types to Generate</Label>
                <div className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="coverLetter"
                      checked={letterTypes.coverLetter}
                      onCheckedChange={(checked) => 
                        setLetterTypes(prev => ({ ...prev, coverLetter: checked as boolean }))
                      }
                    />
                    <Label htmlFor="coverLetter">Cover Letters</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="thankYouLetter"
                      checked={letterTypes.thankYouLetter}
                      onCheckedChange={(checked) => 
                        setLetterTypes(prev => ({ ...prev, thankYouLetter: checked as boolean }))
                      }
                    />
                    <Label htmlFor="thankYouLetter">Thank You Letters</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="outreachLetter"
                      checked={letterTypes.outreachLetter}
                      onCheckedChange={(checked) => 
                        setLetterTypes(prev => ({ ...prev, outreachLetter: checked as boolean }))
                      }
                    />
                    <Label htmlFor="outreachLetter">Outreach Letters</Label>
                  </div>
                </div>
              </div>
            )}

            {/* JSON Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="jsonInput">JSON Data</Label>
                {jsonInput.trim() && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    isMasterFormat 
                      ? 'bg-primary/10 text-primary' 
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {isMasterFormat ? '✦ Master Format' : 'Legacy Format'}
                  </span>
                )}
              </div>
              <Textarea
                id="jsonInput"
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                rows={20}
                className="font-mono text-sm"
                placeholder="Paste your JSON data here..."
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={handleGenerate}
                disabled={generating || generatingZip || generatingForViewer || !jsonInput.trim()}
                className="flex-1"
                size="lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Generate & Download
                  </>
                )}
              </Button>
              <Button 
                onClick={handleGenerateZip}
                disabled={generating || generatingZip || generatingForViewer || isCheckingMembership || !jsonInput.trim()}
                className="flex-1"
                size="lg"
                variant="secondary"
              >
                {isCheckingMembership ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking account...
                  </>
                ) : generatingZip ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Zipping...
                  </>
                ) : (
                  <>
                    <FolderArchive className="w-4 h-4 mr-2" />
                    Download as ZIP
                  </>
                )}
              </Button>
              <Button 
                  onClick={generateAndOpenInViewer}
                  disabled={generating || generatingZip || generatingForViewer || !jsonInput.trim()}
                  className="flex-1"
                  size="lg"
                  variant="outline"
                >
                  {generatingForViewer ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Opening...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Open in Viewer
                    </>
                  )}
                </Button>
              <Button 
                variant="outline"
                onClick={() => setJsonInput(getJsonTemplate())}
                disabled={generating || generatingZip || generatingForViewer}
              >
                Master Example
              </Button>
              <Button 
                variant="ghost"
                onClick={() => setJsonInput(getLegacyJsonTemplate())}
                disabled={generating || generatingZip || generatingForViewer}
                size="sm"
              >
                Legacy
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Expected JSON Format</CardTitle>
            <CardDescription>
              {isMasterFormat 
                ? "Master format: all document types in a single payload with shared client info."
                : "Legacy format: letters only, each with its own header info."
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isMasterFormat ? (
              <pre className="text-xs bg-muted p-4 rounded border overflow-x-auto">
{`{
  "clientInfo": {
    "fullName": "Your Name",
    "phoneNumber": "(555) 123-4567",
    "email": "your.email@example.com",
    "cityST": "City, State",
    "linkedin": "LinkedIn URL"
  },
  "documents": [
    {
      "documentType": "resume",           // → Docmosis resume template
      "resumeIdentifier": "Resume1",
      "jobTitle": "Software Engineer",
      "companyName": "Company Name",
      "data": { /* canonical resume JSON: Name, ProfessionalExperience, SkillRows, etc. */ }
    },
    {
      "documentType": "cover-letter",     // → Docmosis letter template
      "resumeIdentifier": "Resume1",
      "jobTitle": "Software Engineer",
      "companyName": "Company Name",
      "recipientName": "Recipient Name",
      "data": { "body": "Your cover letter content..." }
    },
    {
      "documentType": "thank-you-letter", // same shape as cover-letter
      ...
    },
    {
      "documentType": "outreach-letter",
      ...
    },
    {
      "documentType": "linkedin-profile", // → ZIP root (no job folder)
      "data": { "body": "Your LinkedIn profile content..." }
    },
    {
      "documentType": "executive-bio",    // → ZIP root (no job folder)
      "data": { "body": "Your executive bio content..." }
    }
  ]
}

// documentType values: resume, cover-letter, thank-you-letter,
//   outreach-letter, linkedin-profile, executive-bio`}
              </pre>
            ) : (
              <pre className="text-xs bg-muted p-4 rounded border overflow-x-auto">
{`{
  "letters": [
    {
      "resumeIdentifier": "Resume1", // Optional: helps organize output files
      "headerInfo": {
        "fullName": "Your Name",
        "phoneNumber": "(555) 123-4567",
        "email": "your.email@example.com",
        "cityST": "City, State",
        "linkedin": "LinkedIn URL",
        "companyName": "Company Name",
        "recipientName": "Recipient Name",
        "jobTitle": "Job Title"
      },
      "coverLetterBody": "Your cover letter content...",
      "thankYouLetterBody": "Your thank you letter content...", // Optional
      "outreachLetterBody": "Your outreach letter content..."   // Optional
    }
    // Add more letter objects for additional resumes/jobs
  ]
}`}
              </pre>
            )}
          </CardContent>
        </Card>

        {/* Resume Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Attach Resumes (Optional)
            </CardTitle>
            <CardDescription>
              Upload PDF or Word resumes for each job entry. Drag & drop multiple files to auto-match by filename, or use individual upload buttons. Matched resumes are included in the ZIP download.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Drag & Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf,.doc,.docx';
                input.multiple = true;
                input.onchange = (e) => {
                  const files = (e.target as HTMLInputElement).files;
                  if (files) handleDropFiles(files);
                };
                input.click();
              }}
              className={`flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50 bg-muted/30 hover:bg-muted/50'
              }`}
            >
              {isAIMatching ? (
                <>
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  <p className="text-sm font-medium text-primary">Matching resumes with AI...</p>
                  <p className="text-xs text-muted-foreground">This usually takes a few seconds</p>
                </>
              ) : (
                <>
                  <Upload className={`w-8 h-8 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
                  <p className="text-sm font-medium text-muted-foreground">
                    {isDragging ? 'Drop files to auto-match...' : 'Drag & drop resumes here, or click to browse'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    AI-powered matching — handles abbreviations, word order, and naming variations (PDF, DOC, DOCX)
                  </p>
                </>
              )}
            </div>
            {(() => {
              const entries = getLetterEntries();
              if (entries.length === 0) {
                return (
                  <p className="text-sm text-muted-foreground italic">
                    Paste valid JSON above first — resume upload slots will appear for each entry.
                  </p>
                );
              }
              return (
                <div className="space-y-3">
                  {entries.map((entry) => {
                    const existing = resumeFiles.find(r => r.resumeIdentifier === entry.resumeIdentifier);
                    const folderName = getFolderName(entry.jobTitle);
                    const isEditing = editingFolder === entry.jobTitle;
                    return (
                      <div key={entry.resumeIdentifier} className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                        <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium">{entry.resumeIdentifier} —</span>
                            {isEditing ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  value={editingFolderValue}
                                  onChange={(e) => setEditingFolderValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitFolderEdit(entry.jobTitle);
                                    if (e.key === 'Escape') setEditingFolder(null);
                                  }}
                                  className="h-6 text-sm py-0 px-1.5 w-48"
                                  autoFocus
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0"
                                  onClick={() => commitFolderEdit(entry.jobTitle)}
                                >
                                  <Check className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <span className="text-sm text-muted-foreground">
                                  📁 {folderName}
                                  {folderNameOverrides[entry.jobTitle] && (
                                    <span className="text-xs ml-1 opacity-50">(was: {entry.jobTitle})</span>
                                  )}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-5 w-5 p-0"
                                  onClick={() => startEditingFolder(entry.jobTitle)}
                                  title="Rename folder"
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {existing && existing.files.length > 0 ? (
                            <div className="space-y-1 mt-1">
                              {existing.files.map((f, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                  <span className="text-xs text-green-700 truncate">{f.name}</span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-5 w-5 p-0"
                                    onClick={() => removeResumeFile(entry.resumeIdentifier, idx)}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">No resume attached</p>
                          )}
                        </div>
                        <div>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            className="hidden"
                            id={`resume-${entry.resumeIdentifier}`}
                            onChange={(e) => handleResumeUpload(entry.resumeIdentifier, entry.jobTitle, e)}
                          />
                          <label htmlFor={`resume-${entry.resumeIdentifier}`}>
                            <Button size="sm" variant="outline" asChild>
                              <span>
                                <Upload className="w-3 h-3 mr-1" />
                                {existing ? 'Add More' : 'Upload'}
                              </span>
                            </Button>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                  {resumeFiles.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {resumeFiles.reduce((sum, r) => sum + r.files.length, 0)} resume file(s) attached — will be included in ZIP download.
                    </p>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* LinkedIn / Executive Bio Upload Section — root-level files */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Attach LinkedIn Profile / Executive Bio (Optional)
            </CardTitle>
            <CardDescription>
              Upload LinkedIn profiles, executive bios, or other general documents. These will be placed in the main folder of the ZIP (not inside job subfolders).
              {isMasterFormat && (
                <span className="block mt-1 text-xs">
                  <strong>Tip:</strong> You can also include LinkedIn/bio content in the master JSON to auto-generate them via Docmosis.
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                multiple
                className="hidden"
                id="root-files-upload"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files) {
                    setRootFiles(prev => [...prev, ...Array.from(files)]);
                  }
                  e.target.value = '';
                }}
              />
              <label htmlFor="root-files-upload">
                <Button size="sm" variant="outline" asChild>
                  <span>
                    <Upload className="w-3 h-3 mr-1" />
                    Upload Files
                  </span>
                </Button>
              </label>
            </div>
            {rootFiles.length > 0 && (
              <div className="space-y-1">
                {rootFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 border rounded bg-muted/30">
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm flex-1 truncate">{file.name}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 w-5 p-0"
                      onClick={() => setRootFiles(prev => prev.filter((_, i) => i !== idx))}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground mt-1">
                  {rootFiles.length} file(s) will be placed in the ZIP root folder.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>File Organization</CardTitle>
            <CardDescription>
              Generated files will be organized and named for easy identification
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <p><strong>Naming Convention:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><code>FirstName LastName - JobTitle Resume 2026.docx</code></li>
                <li><code>FirstName LastName - JobTitle Cover Letter 2026.docx</code></li>
                <li><code>FirstName LastName - JobTitle Thank You Letter 2026.docx</code></li>
                <li><code>FirstName LastName - JobTitle Outreach Letter 2026.docx</code></li>
                <li><code>FirstName LastName - LinkedIn Profile 2026.docx</code></li>
                <li><code>FirstName LastName - Executive Bio 2026.docx</code></li>
              </ul>
              <p className="mt-4"><strong>ZIP Download:</strong> Documents organized by job title. LinkedIn/bio files stay in the root:</p>
              <pre className="bg-muted p-3 rounded border text-xs mt-2">{`📁 FirstName LastName - Master Documents.zip
├── John Doe - LinkedIn Profile 2026.docx
├── John Doe - Executive Bio 2026.docx
├── LinkedIn Profile.pdf              (uploaded)
├── Executive Bio.docx                (uploaded)
├── 📁 Software Engineer/
│   ├── John Doe - Software Engineer Resume 2026.docx
│   ├── John Doe - Software Engineer Cover Letter 2026.docx
│   ├── John Doe - Software Engineer Thank You Letter 2026.docx
│   ├── John Doe - Software Engineer Outreach Letter 2026.docx
│   └── John Doe - Software Engineer Resume.pdf   (uploaded)
├── 📁 Senior Developer/
│   ├── John Doe - Senior Developer Resume 2026.docx
│   └── John Doe - Senior Developer Cover Letter 2026.docx`}</pre>
              <p className="mt-4"><strong>Individual Download:</strong> All documents download separately with correct names</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Membership Auto-Create Prompt — mirrors the AdminQuickOnboard
          panel so operators can spin up accounts on all 3 platforms
          (TailorWiz / JobIntel 360 / Jobs On Demand Academy) in one go. */}
      <AlertDialog open={showMembershipPrompt} onOpenChange={(open) => { if (!open && !isCreatingMembership) handleSkipMembership(); }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Create Accounts for {pendingClientInfo?.firstName || 'this client'}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  <strong>{pendingClientInfo?.fullName}</strong> ({pendingClientInfo?.email}) does not have a TailorWiz account yet.
                </p>
                <div className="rounded-md bg-muted p-3 text-xs space-y-1">
                  <p><strong>Email:</strong> {pendingClientInfo?.email}</p>
                  <p><strong>Auto-Password:</strong> {pendingClientInfo?.lastName.toLowerCase()}{pendingClientInfo?.phone.replace(/\D/g, '').slice(-4)}</p>
                </div>
                <p className="text-sm">Pick the platforms to provision:</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Outside the description (asChild needs a single child) so we
              can render real interactive controls. */}
          <div className="space-y-3 px-1">
            {/* TailorWiz */}
            <div className="flex items-center gap-3 p-3 rounded-md border bg-card">
              <Checkbox
                id="tw-en"
                checked={twEnabled}
                onCheckedChange={(v) => setTwEnabled(!!v)}
                disabled={isCreatingMembership}
              />
              <Label htmlFor="tw-en" className="w-32 shrink-0 text-sm font-medium">
                TailorWiz
              </Label>
              <span className="text-xs text-muted-foreground flex-1">
                Pro plan · tailorwiz.com
              </span>
            </div>

            {/* JobIntel 360 (with plan picker) */}
            <div className="flex items-center gap-3 p-3 rounded-md border bg-card">
              <Checkbox
                id="ji-en"
                checked={jiEnabled}
                onCheckedChange={(v) => setJiEnabled(!!v)}
                disabled={isCreatingMembership}
              />
              <Label htmlFor="ji-en" className="w-32 shrink-0 text-sm font-medium">
                JobIntel 360
              </Label>
              <div className="flex-1">
                <Select value={jiPlan} onValueChange={setJiPlan} disabled={!jiEnabled || isCreatingMembership}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOBINTEL_PLANS_BLG.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Jobs On Demand Academy (Course Magic) */}
            <div className="p-3 rounded-md border bg-card">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="ac-en"
                  checked={acEnabled}
                  onCheckedChange={(v) => setAcEnabled(!!v)}
                  disabled={isCreatingMembership}
                />
                <Label htmlFor="ac-en" className="w-32 shrink-0 text-sm font-medium">
                  Jobs On Demand Academy
                </Label>
                <span className="text-xs text-muted-foreground flex-1">
                  app.jobsondemandacademy.com
                </span>
              </div>
              {acEnabled && (
                <div className="mt-3 pl-7 space-y-1.5">
                  <p className="text-xs text-muted-foreground mb-1">Grant courses:</p>
                  {ACADEMY_COURSES_BLG.map((c) => {
                    const checked = acCourseIds.includes(c.id);
                    return (
                      <div key={c.id} className="flex items-start gap-2">
                        <Checkbox
                          id={`ac-c-${c.id}`}
                          checked={checked}
                          onCheckedChange={(v) => {
                            setAcCourseIds((prev) =>
                              v ? [...prev, c.id] : prev.filter((id) => id !== c.id),
                            );
                          }}
                          disabled={isCreatingMembership}
                          className="mt-0.5"
                        />
                        <Label htmlFor={`ac-c-${c.id}`} className="text-xs leading-snug cursor-pointer">
                          {c.label}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreatingMembership} onClick={handleSkipMembership}>
              No, Skip
            </AlertDialogCancel>
            <AlertDialogAction disabled={isCreatingMembership} onClick={handleConfirmCreateMembership}>
              {isCreatingMembership ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Creating...</> : 'Yes, Create Accounts'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
